import React, { useContext, useEffect, useState } from 'react';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { UserContextType, useAuth } from '../../auth';
import { getUsers } from '../../service/AuthService';
import { GroupApiError } from '../../service/GroupService';
import { groupInvitationService } from '../../service/GroupInvitationService';
import { User } from '../../models/User';
import AiRewriteButton from '../../common/AiRewriteButton';

const Emitter = require('tiny-emitter/instance');

const fullName = (user: User) => `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();
const isGroupAdmin = (user: User) => (user.roles ?? []).includes('GroupAdmin');

// Picks a registered user and emails them a link to set up their own group. Admin only.
const InviteGroupLeaderDialog: React.FC<{
    open: boolean;
    onClose: () => void;
    onInvited: () => void;
}> = ({ open, onClose, onInvited }) => {
    const { getToken } = useContext<UserContextType>(useAuth);
    const [users, setUsers] = useState<User[]>([]);
    const [loadingUsers, setLoadingUsers] = useState(false);
    const [user, setUser] = useState<User | null>(null);
    const [description, setDescription] = useState('');
    const [sending, setSending] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!open || !getToken) return;
        setUser(null);
        setDescription('');
        setError('');
        setLoadingUsers(true);
        getUsers(
            getToken(),
            (response: { data: User[] }) => {
                const list = [...(response.data ?? [])].sort((a, b) => fullName(a).localeCompare(fullName(b)));
                setUsers(list);
                setLoadingUsers(false);
            },
            () => {
                Emitter.emit('notify', 'Failed to load users.', 'error');
                setLoadingUsers(false);
            }
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const handleSend = async () => {
        if (!getToken) return;
        if (!user || !description.trim()) {
            setError('Please choose a person and describe the group.');
            return;
        }
        setError('');
        setSending(true);
        try {
            await groupInvitationService.invite({ userId: user.id, description: description.trim() }, getToken());
            Emitter.emit('notify', `Invitation sent to ${fullName(user) || user.email}.`, 'success');
            onInvited();
            onClose();
        } catch (err) {
            if (err instanceof GroupApiError && err.status === 409) {
                setError(`${fullName(user) || user.email} already has a pending invitation — use Resend.`);
            } else if (err instanceof GroupApiError && err.status === 502) {
                setError("Couldn't send the invitation email. Nothing was saved; please try again.");
            } else if (err instanceof GroupApiError && (err.status === 400 || err.status === 404)) {
                setError(err.message);
            } else {
                setError('Failed to send the invitation.');
            }
        } finally {
            setSending(false);
        }
    };

    return (
        <Dialog open={open} onClose={() => !sending && onClose()} fullWidth maxWidth="sm">
            <DialogTitle>Invite Group Leader</DialogTitle>
            <DialogContent>
                <DialogContentText sx={{ mb: 2 }}>
                    They'll get an email with step-by-step instructions and a link to set up their group, and will be
                    given the Group Admin role. The group starts hidden until they turn it on.
                </DialogContentText>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Autocomplete
                        options={users}
                        value={user}
                        onChange={(_, value) => setUser(value)}
                        loading={loadingUsers}
                        disabled={sending}
                        getOptionLabel={(option) => `${fullName(option)} — ${option.email}`}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        filterOptions={(options, { inputValue }) => {
                            const q = inputValue.trim().toLowerCase();
                            if (!q) return options;
                            return options.filter((o) =>
                                `${fullName(o)} ${o.email}`.toLowerCase().includes(q)
                            );
                        }}
                        renderOption={(props, option) => (
                            <li {...props} key={option.id}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, width: '100%' }}>
                                    <Typography noWrap sx={{ flexGrow: 1, minWidth: 0 }}>
                                        {fullName(option)} — <Typography component="span" color="text.secondary">{option.email}</Typography>
                                    </Typography>
                                    {isGroupAdmin(option) && <Chip label="Group Admin" size="small" />}
                                </Box>
                            </li>
                        )}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Person to invite"
                                placeholder="Search by name or email"
                                required
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {loadingUsers && <CircularProgress color="inherit" size={18} />}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                            />
                        )}
                    />
                    <TextField
                        label="About the group"
                        placeholder="e.g. A Tuesday-night Bible study for young couples. We'd love you to lead it!"
                        helperText="Included in the email and used to start their group description."
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        multiline
                        minRows={4}
                        required
                        fullWidth
                        disabled={sending}
                    />
                    <Stack direction="row">
                        <AiRewriteButton
                            content={description}
                            contentType="group"
                            disabled={sending}
                            onAccept={setDescription}
                        />
                    </Stack>
                    {error && <Alert severity="error">{error}</Alert>}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={sending}>Cancel</Button>
                <Button
                    onClick={handleSend}
                    variant="contained"
                    disabled={sending || !user || !description.trim()}
                    startIcon={sending ? <CircularProgress color="inherit" size={16} /> : undefined}
                >
                    {sending ? 'Sending...' : 'Send Invitation'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default InviteGroupLeaderDialog;
